import { PaginatedResponse, PaginationParams } from '@pubstack/common/src/pagination'
import { AdUnitEnv, CreatePbsAdUnit, PbsAdUnit, ValidateAdUnitNamesRequest, ValidateAdUnitNamesResponse } from '@pubstack/common/src/pbs/adunit'
import { sortByName } from '@pubstack/common/src/sort'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { buildUrlWithSearchParams } from '~/utils/url'
import { get, MutationToasts, post, useMutation, useQuery } from '../api-client'

export const AD_UNITS_QUERY_KEYS = ['pbs', 'adunits']

// The env is part of the key, not just the request: a name is taken per env, so an s2s answer must never
// be served to an in-app form from the cache.
const adUnitNameValidationKey = (name: string, env: AdUnitEnv) => [...AD_UNITS_QUERY_KEYS, 'validate-name', env, name]

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useAdUnit = (id: string) => {
  return useQuery<PbsAdUnit>({ url: `/pbs/adunits/${id}`, keys: [...AD_UNITS_QUERY_KEYS, id] })
}

export const useAdUnits = (options?: { env?: AdUnitEnv[]; enabled?: boolean }, enabled = true) => {
  const url = buildUrlWithSearchParams('/pbs/adunits', options || {})
  const queryResult = useQuery<PbsAdUnit[]>({ url, keys: [...AD_UNITS_QUERY_KEYS, options || {}], enabled, select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

/**
 * One page of a tab's ad units, narrowed and ordered server side.
 *
 * Kept apart from `useAdUnits` rather than replacing it: the floor price picker, the bidder seat form and the
 * stack flyout resolve ad units by name and id across the whole scope, so a page would silently break them.
 */
export const useAdUnitsWithPagination = ({
  filters,
  pagination,
  sortBy,
  sortOrder,
  enabled = true,
}: {
  filters?: {
    search?: string | null
    /** The single environment the tab lists. */
    env?: AdUnitEnv
    /** The archived ad units instead of the live ones — never both. Owner-gated server side. */
    archived?: boolean
  }
  pagination: PaginationParams
  sortBy?: string
  sortOrder?: 'ascending' | 'descending'
  enabled?: boolean
}) => {
  const url = buildUrlWithSearchParams('/pbs/adunits/pagination', { ...filters, ...pagination, sortBy, sortOrder })
  const queryResult = useQuery<PaginatedResponse<PbsAdUnit>>({
    url,
    keys: [...AD_UNITS_QUERY_KEYS, 'pagination', { pagination, sortBy, sortOrder, filters }],
    enabled,
    keepPreviousDataEnabled: true,
  })

  return { ...queryResult, data: queryResult.data || { data: [], pagination: { total: 0, totalPages: 1, ...pagination } } }
}

/** Every ad unit the tab's filters select, for Export — fetched on click rather than held in the page. */
export const fetchAdUnitsForExport = async (filters: { search?: string | null; env?: AdUnitEnv; archived?: boolean }, sortBy?: string, sortOrder?: 'ascending' | 'descending'): Promise<PbsAdUnit[]> =>
  get<PbsAdUnit[]>(buildUrlWithSearchParams('/pbs/adunits/export', { ...filters, sortBy, sortOrder }))

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateAdUnit = (toasts: MutationToasts = {}) => {
  return useMutation<CreatePbsAdUnit>({
    method: 'post',
    url: '/pbs/adunits',
    collectionKeys: AD_UNITS_QUERY_KEYS,
    ...toasts,
  })
}

export const useBulkCreateAdUnits = (toasts: MutationToasts = {}) => {
  return useMutation<CreatePbsAdUnit[]>({
    method: 'post',
    url: '/pbs/adunits/bulk',
    collectionKeys: AD_UNITS_QUERY_KEYS,
    isOptimistic: false,
    ...toasts,
  })
}

export const useUpdateAdUnit = (adUnitId: string, toasts: MutationToasts = {}) => {
  return useMutation<PbsAdUnit>({
    method: 'put',
    url: `/pbs/adunits/${adUnitId}`,
    collectionKeys: AD_UNITS_QUERY_KEYS,
    resourceId: adUnitId,
    ...toasts,
  })
}

export const useImportAdUnitsFromCsv = (env: AdUnitEnv, dryRun: boolean = false, toasts: MutationToasts = {}) => {
  const url = buildUrlWithSearchParams(`/pbs/adunits/bulk/csv/${env}`, { dryRun })
  return useMutation<FormData, CreatePbsAdUnit[]>({
    method: 'post',
    url,
    collectionKeys: AD_UNITS_QUERY_KEYS,
    ...toasts,
  })
}

//-------------------------------------------------------------------------
// Validators
//-------------------------------------------------------------------------

export const validateAdUnitNames = async (names: string[], env: AdUnitEnv, signal: AbortSignal): Promise<string[]> => {
  const { takenNames } = await post<ValidateAdUnitNamesResponse>('/pbs/adunits/validate-names', { names, env } satisfies ValidateAdUnitNamesRequest, signal)
  return takenNames
}

export const useValidateAdUnitNameUnicity = (env: AdUnitEnv) => {
  const queryClient = useQueryClient()

  const validate = useCallback(
    async (value: unknown) => {
      if (typeof value !== 'string') return true
      if (!value.trim()) return true
      try {
        const trimmedValue = value.trim()
        const result = await queryClient.fetchQuery({
          queryKey: adUnitNameValidationKey(trimmedValue, env),
          queryFn: () => get<{ isUnique: boolean }>(buildUrlWithSearchParams(`/pbs/adunits/validate-name/${encodeURIComponent(trimmedValue)}`, { env })),
          staleTime: 3000,
        })
        return result.isUnique || false
      } catch (_e) {
        return false
      }
    },
    [queryClient, env]
  )
  return validate
}
